import {
  Building2,
  CalendarDays,
  ClipboardList,
  FlaskConical,
  Heart,
  Package,
  ShoppingCart,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export type PreviewRole = "patient" | "doctor" | "specialist" | "technician" | "student";

export type WorkspaceQuickAction = {
  label: string;
  to: string;
  icon: typeof CalendarDays;
  avatarUrl?: string | null;
  avatarName?: string | null;
  module?: string;
  badge?: string | number | null;
};

export type BusinessWorkspaceRole = "owner" | "staff" | "technical" | "doctor";

const quickActions: Record<PreviewRole, WorkspaceQuickAction[]> = {
  patient: [
    { label: "Terminet", to: "/app/patient/appointments", icon: CalendarDays },
    { label: "Historiku", to: "/app/patient/history", icon: ClipboardList },
    { label: "Monitorimi", to: "/app/patient/monitoring", icon: Heart },
    { label: "Familja", to: "/app/patient/family", icon: UserRound },
    { label: "Sigurimet", to: "/app/insurance", icon: Building2 },
  ],
  doctor: [
    { label: "Pacientet", to: "/app/professional/patients", icon: UserRound },
  ],
  specialist: [],
  technician: [
    { label: "Lab Admin", to: "/app/laboratory/admin", icon: FlaskConical },
    { label: "Laboratori", to: "/app/laboratory", icon: FlaskConical },
    { label: "Rezultatet", to: "/app/laboratory/results", icon: ClipboardList },
    { label: "Klinikat", to: "/app/professional/businesses", icon: Building2 },
    { label: "Service Hub", to: "/app/service-hub", icon: Package },
  ],
  student: [
    { label: "Profili", to: "/app/professional/profile", icon: UserRound },
    { label: "Medimarket", to: "/commerce", icon: ShoppingCart },
    { label: "Ofertat", to: "/app/jobs", icon: ClipboardList },
  ],
};

quickActions.specialist = quickActions.doctor;

// eslint-disable-next-line react-refresh/only-export-components
export const getPreviewQuickActions = (role: PreviewRole): WorkspaceQuickAction[] => quickActions[role];

// eslint-disable-next-line react-refresh/only-export-components
export const getBusinessWorkspaceQuickActions = (role: BusinessWorkspaceRole): WorkspaceQuickAction[] => {
  const shared: WorkspaceQuickAction[] = [
    { label: "Profili", to: "/app/business/manage?tab=profile", icon: Building2 },
    { label: "Stafi", to: "/app/business/manage?tab=staff", icon: Users },
    { label: "Departamentet", to: "/app/business/manage?tab=departments", icon: ClipboardList },
    { label: "Shërbimet", to: "/app/business/manage?tab=services", icon: Package },
    { label: "Service Hub", to: "/app/service-hub", icon: Package },
  ];

  if (role !== "owner") return shared;

  return [
    ...shared,
    { label: "Marketing", to: "/app/business/manage?tab=marketing", icon: Package },
    { label: "Faturat", to: "/app/business/billing", icon: Wallet },
    { label: "Dyqani", to: "/app/business/commerce", icon: ShoppingCart },
    { label: "Inventari", to: "/app/inventory", icon: ClipboardList },
  ];
};

export const WorkspaceShortcutCarousel = ({ actions, variant = "cards" }: { actions: WorkspaceQuickAction[]; variant?: "cards" | "tabs" }): JSX.Element => {
  const { pathname } = useLocation();

  if (variant === "tabs") {
    return <section aria-label="Menuja e biznesit" className="-mx-1 overflow-x-auto px-1 pb-2 [scrollbar-width:thin]"><div className="flex min-w-max items-end gap-1 border-b border-border px-2">{actions.map((action) => {
      const Icon = action.icon;
      const active = pathname === action.to.split("?")[0];
      return <Link key={`${action.label}-${action.to}`} to={action.to} title={action.label} aria-label={action.label} className={`relative flex h-12 items-center gap-2 rounded-t-lg border border-b-0 px-3 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${active ? "-mb-px border-primary bg-card font-semibold text-foreground shadow-[0_-2px_10px_rgba(15,23,42,0.05)]" : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"}`}><span className={`grid h-7 w-7 place-items-center rounded-md ${active ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}><Icon className="h-3.5 w-3.5" /></span><span>{action.label}</span></Link>;
    })}</div></section>;
  }

  return <section aria-label="Menuja e profilit" className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2 [scrollbar-width:thin]">{actions.map((action) => {
    const Icon = action.icon;
    return <Link key={`${action.label}-${action.to}`} to={action.to} title={action.label} aria-label={action.label} className="group relative flex h-16 min-w-16 shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-3 text-muted-foreground transition hover:border-primary/50 hover:bg-primary/[0.06] hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-w-32">{action.avatarUrl ? <img src={action.avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full border border-border object-cover" /> : action.avatarName ? <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{action.avatarName.trim().charAt(0).toUpperCase()}</span> : <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground"><Icon className="h-4 w-4" /></span>}<span className="hidden max-w-20 truncate text-xs font-semibold sm:block">{action.label}</span>{action.badge !== null && action.badge !== undefined ? <span className="absolute bottom-1.5 right-1.5 min-w-4 rounded-full bg-primary px-1 py-0.5 text-center text-[10px] font-bold leading-none text-primary-foreground shadow-sm">{action.badge}</span> : null}</Link>;
  })}</section>;
};
